<!-- src/app/shared/driver-application-form/driver-application-form.component.html -->

@if (submitted()) {
<div class="flex flex-col items-center justify-center gap-4 pt-2 text-center">
  <div class="text-5xl">✅</div>
  <h2 class="text-2xl font-bold text-gray-900">Application Submitted!</h2>
  <p class="max-w-sm text-gray-500">
    Thank you for applying. A recruiter will be in touch with you soon.
  </p>

  <app-cdai-download />
</div>
} @else if (disqualified()) {
<div class="flex flex-col gap-6">
  <!-- Disqualification notice -->
  <div class="flex flex-col items-center gap-1 pt-2 text-center">
    <h2 class="text-xl font-semibold text-gray-900">
      You didn't qualify for this position
    </h2>
    <p class="max-w-md text-sm text-gray-500">{{ disqualifiedMessage() }}</p>
  </div>

  <!-- Matched application offer: resubmit the same application with no company/job -->
  @if (canOfferMatch()) {
    @if (matched()) {
    <div class="flex flex-col items-center gap-2 rounded-2xl border border-green-200 bg-green-50 p-6 text-center">
      <div class="text-4xl">✅</div>
      <h3 class="text-2xl font-bold text-gray-900">Application sent!</h3>
      <p class="max-w-md text-gray-600">
        We're sharing it with carriers hiring drivers like you. If there's a
        fit, a recruiter will reach out.
      </p>
    </div>
    } @else {
    <div class="flex flex-col items-center gap-3 rounded-2xl border border-primary-200 bg-primary-50 p-6 text-center">
      <h3 class="text-2xl font-bold text-balance text-gray-900">
        Let the right carrier come to you
      </h3>
      <p class="max-w-md text-gray-600">
        This one wasn't a fit, but your application is ready to go. With one
        tap, we'll share it with carriers hiring drivers like you{{
          form.value.zipCode ? ' near ' + form.value.zipCode : ''
        }}. If there's a fit, they'll reach out to you. No more forms.
      </p>
      <button
        mat-flat-button
        color="primary"
        type="button"
        class="mt-1"
        [disabled]="matching()"
        (click)="onMatchMe()"
      >
        @if (matching()) {
        <mat-spinner diameter="20" />
        } @else {
        Match Me with Carriers
        }
      </button>
      <p class="m-0 text-xs text-gray-500">
        We'll share your application with carriers that match your profile.
      </p>
    </div>
    }
  }

  <!-- Qualifying jobs -->
  @if (loadingJobs()) {
  <div class="flex justify-center py-4">
    <mat-spinner diameter="32" />
  </div>
  } @else if (qualifyingJobs().length > 0) {
  <div class="flex flex-col gap-4">
    <h3 class="text-lg font-semibold text-gray-800">
      Here are some positions you may qualify for:
    </h3>
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      @for (job of qualifyingJobs(); track job.id) {
      <app-job-search-card [job]="job" />
      }
    </div>
  </div>
  } @else {
  <div class="flex flex-col items-center gap-3 py-2 text-center">
    <p class="text-gray-500">
      We couldn't find an exact match right now, but there are plenty more
      opportunities waiting.
    </p>
    <a mat-flat-button color="primary" routerLink="/jobs" (click)="modal().close()">
      Browse All Jobs
    </a>
  </div>
  }
</div>
} @else {
<form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex w-full flex-col gap-2" novalidate>
  <!-- ── Personal Info ─────────────────────────────────────────────── -->
  <div class="grid grid-cols-1 gap-4 sm:grid-cols-4">
    <mat-form-field appearance="outline" class="w-full">
      <mat-label>First Name</mat-label>
      <input matInput formControlName="firstName" autocomplete="given-name" />
      @if (form.get('firstName')?.invalid && form.get('firstName')?.touched) {
      <mat-error>First name is required.</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="w-full">
      <mat-label>Last Name</mat-label>
      <input matInput formControlName="lastName" autocomplete="family-name" />
      @if (form.get('lastName')?.invalid && form.get('lastName')?.touched) {
      <mat-error>Last name is required.</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="w-full">
      <mat-label>Email</mat-label>
      <input matInput type="email" formControlName="email" autocomplete="email" />
      @if (
      form.get('email')?.hasError('required') && form.get('email')?.touched
      ) {
      <mat-error>Email is required.</mat-error>
      } @else if (
      form.get('email')?.hasError('email') && form.get('email')?.touched
      ) {
      <mat-error>Please enter a valid email.</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="w-full">
      <mat-label>Phone</mat-label>
      <input matInput type="tel" formControlName="phone" autocomplete="tel" placeholder="(555) 555-5555"
        (input)="onPhoneInput($event)" (change)="onPhoneInput($event)" />
      @if (
      form.get('phone')?.hasError('required') && form.get('phone')?.touched
      ) {
      <mat-error>Phone is required.</mat-error>
      } @else if (
      (form.get('phone')?.hasError('invalidUsPhone') ||
      form.get('phone')?.hasError('mask')) &&
      form.get('phone')?.touched
      ) {
      <mat-error>Please enter a valid US phone number.</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="w-full">
      <mat-label>Zip Code</mat-label>
      <input matInput formControlName="zipCode" autocomplete="postal-code" inputmode="numeric" />
      @if (
      form.get('zipCode')?.hasError('required') &&
      form.get('zipCode')?.touched
      ) {
      <mat-error>Zip code is required.</mat-error>
      } @else if (
      form.get('zipCode')?.hasError('pattern') &&
      form.get('zipCode')?.touched
      ) {
      <mat-error>Please enter a valid 5-digit zip code.</mat-error>
      }
    </mat-form-field>

    <!-- ── CDL Classes ───────────────────────────────────────────────── -->
    <div class="flex flex-col col-span-1 sm:col-span-3">
      <p class="text-sm font-medium text-gray-700">
        Which licenses do you currently hold?
      </p>
      <p class="text-xs text-gray-500">
        Have you obtained or are in the process of obtaining any of these
        licenses? If you're currently a student driver and expect to secure your
        Class A certification soon, check Class A.
      </p>
      <div class="mt-1 flex flex-wrap gap-2 sm:gap-4">
        @for (cls of cdlClasses; track cls) {
        <mat-checkbox [checked]="cdlClassChecked[cls]" (change)="toggleCheck(cdlClassChecked, cls)">
          {{ cls }}
        </mat-checkbox>
        }
      </div>
    </div>
  </div>

  <mat-divider class="my-2" />



  <!-- ── Endorsements (CDL A only) ─────────────────────────────────── -->
  @if (showEndorsements) {
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      Do you have any endorsements?
    </p>
    <div class="mt-1 grid grid-cols-1 sm:grid-cols-3">
      @for (end of endorsements; track end) {
      <mat-checkbox [checked]="endorsementChecked[end]" (change)="toggleCheck(endorsementChecked, end)">
        {{ end }}
      </mat-checkbox>
      }
    </div>
  </div>
  }

  <mat-divider />

  <!-- ── Experience ────────────────────────────────────────────────── -->
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      How much verifiable CDL / OTR experience do you have?
      <span class="text-red-500">*</span>
    </p>
    <mat-radio-group formControlName="experienceType" class="grid grid-cols-1 sm:grid-cols-3">
      @for (opt of experienceOptions; track opt) {
      <mat-radio-button [value]="opt">{{ opt }}</mat-radio-button>
      }
    </mat-radio-group>
    @if (
    form.get('experienceType')?.invalid &&
    form.get('experienceType')?.touched
    ) {
    <mat-error class="text-xs">Please select your experience level.</mat-error>
    }
  </div>

  <mat-divider />

  <div class="grid grid-cols-1 sm:grid-cols-5 gap-4">
    <!-- ── Military ──────────────────────────────────────────────────── -->
    <div class="flex flex-col">
      <p class="text-sm font-medium text-gray-700">
        Have you served in the U.S. military?
        <span class="text-red-500">*</span>
      </p>
      <mat-radio-group formControlName="militaryExperience" class="flex">
        <mat-radio-button value="YES">Yes</mat-radio-button>
        <mat-radio-button value="NO">No</mat-radio-button>
      </mat-radio-group>
      @if (
      form.get('militaryExperience')?.invalid &&
      form.get('militaryExperience')?.touched
      ) {
      <mat-error class="text-xs">Please select an option.</mat-error>
      }
    </div>

    <!-- ── Safety Record ─────────────────────────────────────────────── -->
    <div class="flex flex-col gap-1 col-span-1 sm:col-span-2">
      <p class="text-sm font-medium text-gray-700">
        Moving violations in the last 3 years:
        <span class="font-bold text-gray-900">{{
          form.get('violations')?.value
          }}</span>
      </p>
      <mat-slider min="0" max="5" step="1" class="w-full">
        <input matSliderThumb formControlName="violations" />
      </mat-slider>
    </div>

    <div class="flex flex-col gap-1 col-span-1 sm:col-span-2">
      <p class="text-sm font-medium text-gray-700">
        Preventable accidents in the last 3 years:
        <span class="font-bold text-gray-900">{{
          form.get('accidents')?.value
          }}</span>
      </p>
      <mat-slider min="0" max="5" step="1" class="w-full">
        <input matSliderThumb formControlName="accidents" />
      </mat-slider>
    </div>
  </div>

  <mat-divider />

  <!-- ── Driver Type ───────────────────────────────────────────────── -->
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      Are you a Company Driver, Lease Purchase Driver, or an Owner Operator?
      <span class="text-red-500">*</span>
    </p>
    <mat-radio-group formControlName="driverType" class="flex">
      <mat-radio-button value="Company Driver">Company Driver</mat-radio-button>
      <mat-radio-button value="Lease Purchase">Lease Purchase</mat-radio-button>
      <mat-radio-button value="Owner Operator">Owner Operator</mat-radio-button>
    </mat-radio-group>
    @if (form.get('driverType')?.invalid && form.get('driverType')?.touched) {
    <mat-error class="text-xs">Please select your driver type.</mat-error>
    }
  </div>

  <!-- ── Interest in OO / LP (Company Driver only) ─────────────────── -->
  @if (showInterestInOoLp) {
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      Are you interested in becoming an Owner Operator or hearing about
      Lease Purchase opportunities?
    </p>
    <mat-radio-group formControlName="interestInOoLp" class="flex">
      <mat-radio-button value="YES">Yes</mat-radio-button>
      <mat-radio-button value="NO">No</mat-radio-button>
    </mat-radio-group>
  </div>
  }

  <!-- ── Own Authority (Owner Operator only) ───────────────────────── -->
  @if (showHasOwnAuthority) {
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      Are you a Power Unit Only Owner Operator or do you have your Own
      Authority?
    </p>
    <mat-radio-group formControlName="hasOwnAuthority" class="flex">
      <mat-radio-button value="Power Unit Only">Power Unit Only</mat-radio-button>
      <mat-radio-button value="Own Authority">Own Authority</mat-radio-button>
    </mat-radio-group>
  </div>
  }

  <mat-divider />

  <!-- ── Solo or Team ──────────────────────────────────────────────── -->
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      Are you a Solo Driver or a Team Driver?
    </p>
    <mat-radio-group formControlName="soloOrTeam" class="flex">
      <mat-radio-button value="Solo Driver">Solo Driver</mat-radio-button>
      <mat-radio-button value="Team Driver">Team Driver</mat-radio-button>
    </mat-radio-group>
  </div>

  <!-- ── Interest in Team Driving (not Team Driver) ────────────────── -->
  @if (showInterestInTeamDriving) {
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      Would you be interested in becoming a team driver or matching up with
      another solo driver if it meant you would make more money and run in
      nicer equipment?
    </p>
    <mat-radio-group formControlName="interestInTeamDriving" class="flex">
      <mat-radio-button value="Yes">Yes</mat-radio-button>
      <mat-radio-button value="No">No</mat-radio-button>
    </mat-radio-group>
  </div>
  }

  <mat-divider />

  <!-- ── Primary Freight Type ──────────────────────────────────────── -->
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      What type of freight do you most commonly haul?
    </p>
    <mat-radio-group formControlName="freightType" class="grid grid-cols-1 sm:grid-cols-3">
      @for (ft of freightTypes; track ft) {
      <mat-radio-button [value]="ft">{{ ft }}</mat-radio-button>
      }
    </mat-radio-group>
  </div>

  <mat-divider />

  <!-- ── Interested Freight Types ──────────────────────────────────── -->
  <div class="flex flex-col">
    <p class="text-sm font-medium text-gray-700">
      What other freight types would you be interested in hauling?
    </p>
    <div class="mt-1 grid grid-cols-1 sm:grid-cols-3">
      @for (ft of freightTypes; track ft) {
      <mat-checkbox [checked]="interestedFreightTypeChecked[ft]"
        (change)="toggleCheck(interestedFreightTypeChecked, ft)">
        {{ ft }}
      </mat-checkbox>
      }
    </div>
  </div>

  <mat-divider />

  <!-- ── Contact Time ──────────────────────────────────────────────── -->
  <div class="flex flex-col gap-2">
    <p class="text-sm font-medium text-gray-700">
      What is the best time to contact you?
    </p>
    <mat-radio-group formControlName="applicationContactTime" class="flex flex-col sm:flex-row sm:gap-4">
      @for (time of contactTimes; track time) {
      <mat-radio-button [value]="time">{{ time }}</mat-radio-button>
      }
    </mat-radio-group>
  </div>

  <mat-divider />

  <!-- ── Legal / Consent ───────────────────────────────────────────── -->
  <div class="flex flex-col">
    <div class="flex flex-col gap-1">
      <mat-checkbox formControlName="privacyPolicy">
        <span class="text-sm">
          Yes, I agree to the
          <a href="https://truckdriversus.com/terms" target="_blank" class="text-blue-600 underline">Terms of
            Service</a>
          &amp;
          <a href="https://truckdriversus.com/privacy" target="_blank" class="text-blue-600 underline">Privacy
            Policy</a>.
          <span class="text-red-500">*</span>
        </span>
      </mat-checkbox>
      @if (
      form.get('privacyPolicy')?.invalid &&
      form.get('privacyPolicy')?.touched
      ) {
      <mat-error class="ml-4 text-xs">You must agree to continue.</mat-error>
      }
    </div>

    <div class="flex flex-col gap-1">
      <mat-checkbox formControlName="marketingMessages">
        <span class="text-sm">
          I agree to receive marketing messages.
          <span class="text-red-500">*</span>
        </span>
      </mat-checkbox>
      @if (
      form.get('marketingMessages')?.invalid &&
      form.get('marketingMessages')?.touched
      ) {
      <mat-error class="ml-4 text-xs">You must agree to continue.</mat-error>
      }
    </div>

    <p class="leading-relaxed text-xs text-gray-500">
      By submitting this application, you consent to receive communications
      from us via phone call, text message, and/or email, including those sent
      using automated technology, prerecorded messages, and AI-assisted
      systems, regarding your application and other relevant opportunities.
      Message and data rates may apply. You may opt out at any time by
      following the instructions provided in our communications.
    </p>
  </div>

  <!-- ── Submit ────────────────────────────────────────────────────── -->
  <button mat-flat-button color="primary" type="submit" class="w-full" [disabled]="form.invalid || submitting()">
    @if (submitting()) {
    <mat-spinner diameter="20" class="mr-2 inline-block" />
    Submitting...
    } @else {
    Submit Application
    }
  </button>
</form>
}